import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { Search, Key, UserX, UserCheck, Eye } from 'lucide-react';
import { formatDate } from '../../../lib/utils';

/**
 * Onglet "Utilisateurs" du SuperAdmin.
 *
 * Props:
 *  - users: list of all users
 *  - searchTerm, onSearchChange
 *  - onViewUser(userId)
 *  - onOpenResetPasswordDialog(user)
 *  - onToggleStatus(userId)
 */
export default function SuperAdminUsersTab({
    users,
    searchTerm,
    onSearchChange,
    onViewUser,
    onOpenResetPasswordDialog,
    onToggleStatus,
}) {
    const filtered = users.filter(u =>
        !searchTerm
        || u.email?.toLowerCase().includes(searchTerm.toLowerCase())
        || `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Card className="bg-gray-800 border-border">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle>Utilisateurs ({users.length})</CardTitle>
                    <div className="relative w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className="pl-10 bg-gray-700 border-gray-600"
                            data-testid="user-search"
                        />
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-border">
                                <TableHead className="text-muted-foreground">Utilisateur</TableHead>
                                <TableHead className="text-muted-foreground">Email</TableHead>
                                <TableHead className="text-muted-foreground">Rôle</TableHead>
                                <TableHead className="text-muted-foreground">Agence</TableHead>
                                <TableHead className="text-muted-foreground">Statut</TableHead>
                                <TableHead className="text-muted-foreground">Inscription</TableHead>
                                <TableHead className="text-muted-foreground">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map((user) => (
                                <TableRow key={user.id} className="border-border">
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-xs font-medium">
                                                {user.first_name?.[0]}{user.last_name?.[0]}
                                            </div>
                                            <span>{user.first_name} {user.last_name}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-foreground">{user.email}</TableCell>
                                    <TableCell>
                                        <Badge variant={user.role === 'admin' ? 'default' : user.role === 'agent' ? 'secondary' : 'outline'}>
                                            {user.role}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-foreground">{user.agency_name}</TableCell>
                                    <TableCell>
                                        <Badge variant={user.is_active ? 'default' : 'destructive'} className={user.is_active ? 'bg-green-600' : ''}>
                                            {user.is_active ? 'Actif' : 'Inactif'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-sm">
                                        {formatDate(user.created_at)}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onViewUser(user.id)}
                                                className="text-muted-foreground hover:text-white"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onOpenResetPasswordDialog(user)}
                                                className="text-muted-foreground hover:text-white"
                                                data-testid={`reset-pwd-${user.id}`}
                                            >
                                                <Key className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onToggleStatus(user.id)}
                                                className="text-muted-foreground hover:text-white"
                                            >
                                                {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
}
